import { AuditOutlined, LockFilled, StarFilled, UnlockOutlined, WarningOutlined } from '@ant-design/icons';
import { Checkbox, Collapse, Divider, Empty, Grid, Progress, Space, Table, Tag, theme, Tooltip, Typography } from 'antd';
import React, { useState } from 'react';
import { helpers, hooks, models } from '@vote4dance/judging-sdk';
var Stream = hooks.streams.Stream;
var useSession = hooks.sessions.useSession;
var RoundItem = models.rounds.RoundItem;
var HeatsTeamItem = models.teams.HeatsTeamItem;
var ClassItem = models.classes.ClassItem;
var ClassItemSource = models.classes.ClassItemSource;
var JudgeItem = models.judges.JudgeItem;
var ClassRoundSource = models.rounds.ClassRoundSource;
var RoundStateSource = models.results.RoundStateSource;
var RoundTeamSource = models.teams.RoundTeamSource;
var RoundPartialResultSource = models.results.RoundPartialResultSource;
var RoundResultSource = models.results.RoundResultSource;
var RoundResultCommentSource = models.results.RoundResultCommentSource;
var CheckinSource = models.checkins.CheckinSource;
var RoundReceiptSource = models.rounds.RoundReceiptSource;
var PanelJudgeSource = models.judges.PanelJudgeSource;
var RoundJudgeSource = models.judges.RoundJudgeSource;
var CompetitionItem = models.competitions.CompetitionItem;
var RoundStateHelper = helpers.RoundStateHelper;
import { JudgeType, JudgingType, RoundStatus } from '@vote4dance/judging-schema';
import { Localization, _T } from '../src/Localization';
import { Loading } from './Loading';
import { useFederationSettings } from '../hooks/Federation';
import { useCompetition } from '../hooks/Competition';
import { getFeatureFlag, useFeatureFlags } from '../hooks/Features';
const { useToken } = theme;
const { useBreakpoint } = Grid;
const { Title, Text } = Typography;
function getNumericRoundRating(value) {
    if (value === undefined || value === '') {
        return undefined;
    }
    if (value === 'x' || value === 'Yes') {
        return 1;
    }
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(numericValue) ? undefined : numericValue;
}
const renderJudgeList = (judges, options) => {
    if (!judges || judges.length === 0) {
        return null;
    }
    const visibleJudges = (options === null || options === void 0 ? void 0 : options.showTrainees) ? judges : judges.filter(j => j.judge_type !== JudgeType.Trainee);
    if (!visibleJudges.length) {
        return null;
    }
    return (React.createElement(Space, { wrap: true, size: [8, 4], separator: React.createElement(Divider, { orientation: "vertical" }) }, visibleJudges.map(judge => {
        const fullName = JudgeItem.getFullName(judge);
        return (React.createElement(Text, { key: judge.judge_id },
            React.createElement("strong", null, `${judge.judge_letter}) `),
            judge.judge_type === JudgeType.Trainee ? (React.createElement("i", null, fullName)) : (fullName)));
    })));
};
export const ClassResult = ({ item, preview, publicResults = false, showPreliminaryJudges, showAllTraineesInPreview = false }) => {
    var _a, _b;
    const features = useFeatureFlags();
    const resultsEnabled = getFeatureFlag(features, 'resultsEnabled', true);
    const [rounds, loadingRounds] = Stream(item.class_id ? ClassRoundSource(item.class_id) : undefined);
    const roundList = rounds !== null && rounds !== void 0 ? rounds : [];
    const parentRounds = roundList
        .filter(round => parseInt(round.round_parent_id) === 0)
        .reverse();
    const isPublishedRound = (round) => round.round_status === RoundStatus.Confirmed &&
        (round.round_published > 0 || preview);
    const getRoundNoResultsReason = (round) => {
        var _a, _b;
        const comment = (_a = round.round_comment) === null || _a === void 0 ? void 0 : _a.trim();
        if (comment) {
            return comment;
        }
        if (!preview && ((_b = round.round_published) !== null && _b !== void 0 ? _b : 0) === 0) {
            return _T('results.noResultsReason.notPublished');
        }
        switch (round.round_status) {
            case RoundStatus.Open:
                return _T('results.noResultsReason.open');
            case RoundStatus.Ready:
                return _T('results.noResultsReason.ready');
            case RoundStatus.Closed:
                return _T('results.noResultsReason.closed');
            case RoundStatus.Confirmed:
                return _T('results.noResultsReason.confirmed');
            default:
                return _T('results.noResultsReason.default');
        }
    };
    const settings = {
        showMajority: false
    };
    const publishedRounds = parentRounds.filter(isPublishedRound);
    const hasPublishedRounds = parentRounds.some(round => { var _a; return ((_a = round.round_published) !== null && _a !== void 0 ? _a : 0) > 0; });
    const publishedRoundPanelIds = publishedRounds
        .map((round) => { var _a; return (_a = round.panel_id) !== null && _a !== void 0 ? _a : item.default_panel_id; })
        .filter((panelId) => !!panelId);
    const sharedPanelId = (_a = item.default_panel_id) !== null && _a !== void 0 ? _a : (publishedRoundPanelIds.length > 0 &&
        new Set(publishedRoundPanelIds.map(panelId => String(panelId))).size === 1
        ? publishedRoundPanelIds[0]
        : undefined);
    const shouldShowClassPanel = Boolean(sharedPanelId) &&
        (Boolean(showPreliminaryJudges) || hasPublishedRounds);
    const activeRoundKeys = (_b = publishedRounds
        .map(round => round.round_id)
        .filter((id) => !!id)) !== null && _b !== void 0 ? _b : [];
    if (!resultsEnabled && !preview) {
        return (React.createElement(React.Fragment, null,
            React.createElement(Title, null, ClassItem.getClassLabel(item)),
            React.createElement("div", { style: { marginTop: 16 } },
                React.createElement(Empty, { description: _T('results.disabled') }))));
    }
    if (!parentRounds.length && loadingRounds) {
        return React.createElement(Loading, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Title, null, ClassItem.getClassLabel(item)),
        parentRounds.length > 0 && (React.createElement(Collapse, { defaultActiveKey: activeRoundKeys, ghost: true, size: "small", styles: { body: { padding: 0 } }, items: parentRounds.map((round, index) => {
                var _a;
                const roundId = round.round_id;
                const hasResults = isPublishedRound(round) && !!roundId;
                const key = (_a = round.round_id) !== null && _a !== void 0 ? _a : `round-${index}`;
                return {
                    key,
                    label: RoundItem.getRoundLabel(round),
                    children: hasResults ? (React.createElement(RoundResult, { competition_id: item.competition_id, class_id: item.class_id, round_id: roundId, settings: settings, sharedPanelId: sharedPanelId, preview: preview, publicResults: publicResults, hideRoundTitle: true, showAllTraineesInPreview: showAllTraineesInPreview })) : (React.createElement("div", { style: { padding: '12px 0' } },
                        React.createElement(Text, { type: "secondary" }, getRoundNoResultsReason(round))))
                };
            }) })),
        !roundList.length && !loadingRounds && (React.createElement("div", { style: { marginTop: 16 } },
            React.createElement(Empty, { description: _T('results.noRounds') }))),
        shouldShowClassPanel && (React.createElement("div", { style: { width: '100%', marginTop: 16 } },
            !publicResults && React.createElement(Divider, { style: { marginTop: 0 } }),
            React.createElement(ClassJudgeList, { competition_id: item.competition_id, panel_id: sharedPanelId })))));
};
export const ClassJudgeList = ({ competition_id, panel_id }) => {
    const panelSource = panel_id ? PanelJudgeSource(competition_id, panel_id) : undefined;
    const [judges] = Stream(panelSource);
    if (!panel_id) {
        return null;
    }
    return renderJudgeList(judges);
};
export const RoundJudgeList = ({ competition_id, panel_id, showTrainees }) => {
    const panelSource = panel_id ? PanelJudgeSource(competition_id, panel_id) : undefined;
    const [judges] = Stream(panelSource);
    if (!panel_id) {
        return null;
    }
    return renderJudgeList(judges, { showTrainees });
};
export const RoundResult = (props) => {
    var _a, _b, _c, _d;
    const { competition_id, class_id, round_id, settings, sharedPanelId, preview, publicResults = false, hideRoundTitle, enableColumnSorters = false, showJudgeMarkCountSummary = false, showAllTraineesInPreview = false } = props;
    const sessionState = useSession();
    const sessionUserId = (_b = (_a = sessionState === null || sessionState === void 0 ? void 0 : sessionState.sessionInfo) === null || _a === void 0 ? void 0 : _a.user_id) !== null && _b !== void 0 ? _b : (_c = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _c === void 0 ? void 0 : _c.user_id;
    const [states, loadingStates] = Stream(RoundStateSource(round_id));
    const [rounds, loadingRounds] = Stream(ClassRoundSource(class_id));
    const [classItem] = Stream(ClassItemSource(competition_id, class_id));
    const [teams, loadingTeams] = Stream(RoundTeamSource(round_id));
    const [competition, , , loadingCompetition] = useCompetition(competition_id);
    const foundRound = rounds && rounds.find((a) => a.round_id === round_id);
    const [checkins, loadingCheckins] = Stream(foundRound && RoundItem.filteredCoach(foundRound) && !!sessionUserId
        ? CheckinSource(competition_id)
        : undefined);
    const hideMarks = competition ? (CompetitionItem.shouldHideMarks(competition) && !preview) : false;
    const [showTraineesMap, setShowTraineesMap] = useState({});
    if (loadingStates || loadingRounds || loadingTeams || loadingCompetition || loadingCheckins) {
        return React.createElement(Loading, null);
    }
    if (!foundRound) {
        return null;
    }
    const filteredStates = (_d = states === null || states === void 0 ? void 0 : states.filter(a => a.round_id === foundRound.round_id || a.round_parent_id === foundRound.round_id)) !== null && _d !== void 0 ? _d : [];
    const linkedChildStates = filteredStates.filter(state => state.round_parent_id === foundRound.round_id && state.round_id !== foundRound.round_id);
    const updateShowTrainees = (stateId, value) => {
        setShowTraineesMap(prev => {
            if (prev[stateId] === value) {
                return prev;
            }
            return { ...prev, [stateId]: value };
        });
    };
    const defaultShowTrainees = (state) => {
        const canShow = preview && state.round_children == 0;
        if (!canShow) {
            return false;
        }
        if (showAllTraineesInPreview) {
            return true;
        }
        return !!(state.judge_id && state.judge_type === JudgeType.Trainee);
    };
    const hasMarkValue = (value) => value !== undefined && value !== null && value !== '';
    const getRoundJudgeMarkCounts = (stateData) => {
        var _a;
        if (!((_a = stateData === null || stateData === void 0 ? void 0 : stateData.judges) === null || _a === void 0 ? void 0 : _a.length)) {
            return undefined;
        }
        const countSources = stateData.children && stateData.children.length > 0
            ? stateData.children
            : [stateData];
        return stateData.judges.reduce((acc, judgeId) => {
            const marks = countSources.reduce((sum, source) => {
                var _a, _b;
                const values = ((_a = source.ratings) === null || _a === void 0 ? void 0 : _a[judgeId]) || ((_b = source.scores) === null || _b === void 0 ? void 0 : _b[judgeId]) || [];
                return sum + values.filter(hasMarkValue).length;
            }, 0);
            acc[judgeId] = marks;
            return acc;
        }, {});
    };
    const getStatePanelId = (state) => {
        var _a, _b, _c, _d;
        const roundPanelId = (_b = (_a = rounds === null || rounds === void 0 ? void 0 : rounds.find((round) => String(round.round_id) === String(state.round_id))) === null || _a === void 0 ? void 0 : _a.panel_id) !== null && _b !== void 0 ? _b : foundRound === null || foundRound === void 0 ? void 0 : foundRound.panel_id;
        return (_d = (_c = state.panel_id) !== null && _c !== void 0 ? _c : roundPanelId) !== null && _d !== void 0 ? _d : classItem === null || classItem === void 0 ? void 0 : classItem.default_panel_id;
    };
    const renderPanelDetails = (panelId, showTrainees) => {
        if (!panelId) {
            return null;
        }
        return (React.createElement(Space, { direction: "vertical", size: 4, style: { width: '100%', marginBottom: 8 } },
            React.createElement(RoundJudgeList, { competition_id: competition_id, panel_id: panelId, showTrainees: showTrainees })));
    };
    return (React.createElement(React.Fragment, null, filteredStates.map((state, index) => {
        var _a, _b, _c, _d;
        const stateId = String(state.round_id);
        const showTrainees = (_a = showTraineesMap[stateId]) !== null && _a !== void 0 ? _a : defaultShowTrainees(state);
        const judgeMarkCounts = getRoundJudgeMarkCounts(state.state_data);
        const statePanelId = getStatePanelId(state);
        const embeddedChildren = (_c = (_b = state.state_data) === null || _b === void 0 ? void 0 : _b.children) !== null && _c !== void 0 ? _c : [];
        return (React.createElement("div", { style: { pageBreakInside: filteredStates.length > 1 || embeddedChildren.length > 0 ? 'avoid' : undefined }, key: (_d = state.round_id) !== null && _d !== void 0 ? _d : `state-${index}` },
            !(hideRoundTitle && state.round_id === foundRound.round_id) && (React.createElement(Title, { level: 5 }, RoundItem.getRoundLabel(state))),
            React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                React.createElement(PrintStateItem, { state: state, stateData: state.state_data, states: filteredStates, childStates: state.round_id === foundRound.round_id ? linkedChildStates : undefined, class_id: class_id, round: foundRound, classRounds: rounds, teams: teams, settings: settings, preview: preview, publicResults: publicResults, hideMarks: hideMarks, showTrainees: showTrainees, onShowTraineesChange: value => updateShowTrainees(stateId, value), enableColumnSorters: enableColumnSorters, showJudgeMarkCountSummary: showJudgeMarkCountSummary, judgeMarkCounts: judgeMarkCounts, competitionFederation: competition === null || competition === void 0 ? void 0 : competition.competition_federation, coachFilter: {
                        checkins,
                        sessionUserId,
                        userCompetitionLevel: competition === null || competition === void 0 ? void 0 : competition.usercompetition_level
                    } }),
                String(statePanelId !== null && statePanelId !== void 0 ? statePanelId : '') !== String(sharedPanelId !== null && sharedPanelId !== void 0 ? sharedPanelId : '') &&
                    renderPanelDetails(statePanelId, showTrainees),
                embeddedChildren.map((childData, childIndex) => {
                    var _a;
                    return (React.createElement(React.Fragment, { key: `${state.round_id}-embedded-${childIndex}` },
                        React.createElement(Title, { level: 5 }, RoundItem.getRoundLabel(state)),
                        React.createElement(PrintStateItem, { state: state, stateData: childData, states: filteredStates, class_id: class_id, round: foundRound, classRounds: rounds, teams: teams, settings: settings, preview: preview, publicResults: publicResults, hideMarks: hideMarks, showTrainees: (_a = showTraineesMap[String(state.round_id)]) !== null && _a !== void 0 ? _a : defaultShowTrainees(state), onShowTraineesChange: value => updateShowTrainees(String(state.round_id), value), enableColumnSorters: enableColumnSorters, showJudgeMarkCountSummary: false, judgeMarkCounts: getRoundJudgeMarkCounts(childData), competitionFederation: competition === null || competition === void 0 ? void 0 : competition.competition_federation, coachFilter: {
                                checkins,
                                sessionUserId,
                                userCompetitionLevel: competition === null || competition === void 0 ? void 0 : competition.usercompetition_level
                            } })));
                }))));
    })));
};
const getRemarkStateLabel = (remark) => {
    const remarkInfo = RoundStateHelper.getRemarkStateInfo(remark);
    if (!remarkInfo) {
        return '';
    }
    const value = _T(remarkInfo.labelKey);
    return value === remarkInfo.labelKey ? remarkInfo.state.toUpperCase() : value;
};
const getRemarkStateHint = (remark) => {
    const remarkInfo = RoundStateHelper.getRemarkStateInfo(remark);
    if (!remarkInfo) {
        return '';
    }
    const value = _T(remarkInfo.hintKey);
    return value === remarkInfo.hintKey ? '' : value;
};
const isLegacyStarPlaceholder = (value) => (typeof value === 'string' && value.includes('glyphicon-star'));
const buildResultColumns = ({ hasExpandableRows, getJudges, getJudgeName, hideNameColumn, settings, token, judgingSettings, state, stateData, round, shouldShowMajorityColumns, getMajorityLimit, shouldShowSumColumn, placementSortValue, startNumberSortValue, compareSortValues, enableColumnSorters }) => {
    var _a;
    return [
        ...(hasExpandableRows ? [{
                title: '',
                dataIndex: 'expandSpacer',
                key: 'expandSpacer',
                width: 32
            }] : []),
        {
            title: '#',
            dataIndex: 'startNumber',
            key: 'startNumber',
            align: 'center',
            sorter: enableColumnSorters ? (a, b) => compareSortValues(startNumberSortValue(a), startNumberSortValue(b)) : undefined,
            render: (value, item) => {
                const startNumberNode = item.cancelled ? React.createElement("del", null, value) : value;
                if (!hideNameColumn || !item.fullName) {
                    return startNumberNode;
                }
                return React.createElement(Tooltip, { title: item.fullName }, startNumberNode);
            }
        },
        ...(!hideNameColumn ? [{
                title: _T('common.name'),
                dataIndex: 'fullName',
                key: 'name',
                render: (value, item) => {
                    return value ? (item.cancelled ? React.createElement("del", null, value) : value) : null;
                }
            }] : []),
        ...getJudges().map(judge => ({
            title: getJudgeName(judge),
            dataIndex: `result_${judge}`,
            align: 'center',
            width: 64,
            render: (value, item) => {
                const comments = item[`comment_${judge}`];
                if (!comments || comments.length === 0) {
                    return value;
                }
                const getCommentName = (list, id) => {
                    const foundLevel = list && list.length > 0 && (list === null || list === void 0 ? void 0 : list.find((a) => a.id === id));
                    return foundLevel && Localization.getLocalizedString(foundLevel.name);
                };
                return (React.createElement("span", null,
                    value,
                    React.createElement("span", { style: { fontSize: 10, color: (settings === null || settings === void 0 ? void 0 : settings.highlight) ? token.colorWarning : 'inherit' } },
                        ' (',
                        comments.map((a, i) => {
                            var _a;
                            return (React.createElement(Tooltip, { title: getCommentName((_a = judgingSettings.comments) === null || _a === void 0 ? void 0 : _a.levels, a.level), key: i },
                                React.createElement("span", { style: ((settings === null || settings === void 0 ? void 0 : settings.highlight) && a.type === "D") ? { color: token.colorError, fontWeight: 'bold' } : {} }, a.level),
                                i < comments.length - 1 ? ', ' : ''));
                        }),
                        ')')));
            },
            onCell: (item) => {
                var _a, _b, _c;
                if (!(settings === null || settings === void 0 ? void 0 : settings.highlight) || !RoundItem.isFinalRound(state))
                    return {};
                const teamIndex = stateData.teams.indexOf(item.teamId);
                const judgeRating = (_b = (_a = stateData.ratings) === null || _a === void 0 ? void 0 : _a[judge]) === null || _b === void 0 ? void 0 : _b[teamIndex];
                const teamPlacement = (_c = stateData.positions) === null || _c === void 0 ? void 0 : _c[teamIndex];
                const numericJudgeRating = getNumericRoundRating(judgeRating);
                if (numericJudgeRating !== undefined && teamPlacement !== undefined) {
                    const valueDifference = Math.abs(teamPlacement - numericJudgeRating);
                    if (valueDifference >= stateData.teams.length / 3) {
                        return { style: { border: `1px solid ${token.colorError}` } };
                    }
                }
                return {};
            }
        })),
        ...(shouldShowMajorityColumns()
            ? ((_a = stateData.majority) === null || _a === void 0 ? void 0 : _a.map((majority, idx) => ({
                title: `1-${idx + 1}`,
                dataIndex: `majority_${idx}`,
                key: `majority_${idx}`,
                align: 'center',
                render: (value, item) => {
                    const count = item[`count_${idx}`];
                    const countString = count && count.length > 0 ? React.createElement("span", { style: { fontSize: 10 } }, `(${count})`) : '';
                    return value > 0 && React.createElement("div", null,
                        value,
                        countString);
                }
            }))) || []
            : []),
        ...(shouldShowSumColumn()
            ? [
                ...(stateData.groupSums
                    ? Array.from({ length: Math.max(0, getJudges().length - (getMajorityLimit() - 1)) }, (_, index) => index).map((_, index) => ({
                        title: _T("round.summaryShort"),
                        dataIndex: `groupSums-${index}`,
                        key: `groupSums-${index}`,
                        align: "center",
                        onHeaderCell: () => ({
                            colSpan: index === 0 ? Math.max(0, getJudges().length - (getMajorityLimit() - 1)) : 0,
                        }),
                        render: (_, item) => {
                            const sum = item.groupSums[index];
                            return (sum !== null && sum !== void 0 ? sum : '-');
                        },
                    }))
                    : []),
                ...(Object.prototype.hasOwnProperty.call(stateData, 'sums') ? [
                    {
                        title: _T("round.summaryShort"),
                        dataIndex: "sum",
                        key: "sum",
                        align: "center",
                        render: (value) => (isLegacyStarPlaceholder(value) ? React.createElement(StarFilled, null) : value)
                    },
                ] : [])
            ]
            : []),
        ...(RoundItem.usesPlacement(round) ? [
            {
                title: _T('round.placementShort'),
                dataIndex: 'placement',
                key: 'placement',
                align: 'center',
                sorter: enableColumnSorters ? (a, b) => {
                    const placementA = placementSortValue(a);
                    const placementB = placementSortValue(b);
                    if (placementA !== null && placementB !== null) {
                        return compareSortValues(placementA, placementB);
                    }
                    if (placementA !== null) {
                        return -1;
                    }
                    if (placementB !== null) {
                        return 1;
                    }
                    return compareSortValues(startNumberSortValue(a), startNumberSortValue(b));
                } : undefined,
                render: (value, item) => {
                    const remarkLabel = getRemarkStateLabel(item.remark);
                    const remarkHint = getRemarkStateHint(item.remark);
                    return (React.createElement(Space, null,
                        value,
                        remarkLabel && (remarkHint ? (React.createElement(Tooltip, { title: remarkHint },
                            React.createElement(Tag, null, remarkLabel))) : (React.createElement(Tag, null, remarkLabel)))));
                }
            }
        ] : [])
    ];
};
const PrintStateItem = (props) => {
    var _a, _b;
    const { state, stateData, states, childStates, round, classRounds, teams, settings, preview, publicResults, hideMarks, showTrainees = false, onShowTraineesChange, enableColumnSorters = false, showJudgeMarkCountSummary = false, judgeMarkCounts, coachFilter, competitionFederation } = props;
    const { shouldShowResultComments } = useFederationSettings(round.competition_id);
    const displayComponents = RoundStateHelper.getDisplayComponents(state);
    const { token } = useToken();
    const screens = useBreakpoint();
    const hideNameColumn = !screens.sm;
    const maxJudgeLabelChars = hideNameColumn ? 2 : 5;
    const [stateTeams] = Stream(state.round_id && state.round_id !== round.round_id ? RoundTeamSource(state.round_id) : undefined);
    const displayTeams = state.round_id === round.round_id ? teams : stateTeams;
    const canShowTrainees = preview && state.round_children == 0;
    const showResultComments = shouldShowResultComments || preview;
    const [traineeResults] = Stream(showTrainees ? RoundResultSource(state.round_id) : undefined);
    const [resultComments] = Stream(showResultComments ? RoundResultCommentSource(state.round_id) : undefined);
    const [receipts] = Stream((settings === null || settings === void 0 ? void 0 : settings.highlight) ? RoundReceiptSource(state.round_id) : undefined);
    const [judges] = Stream(RoundJudgeSource(state.competition_id, state.round_id));
    const isCollectStarsLeafRound = state.judging_type === JudgingType.CollectStars &&
        Number(state.round_children || 0) === 0;
    const showCollectStarsMarks = state.judging_type === JudgingType.CollectStars &&
        (!hideMarks || !!preview);
    const usePartialData = RoundStateHelper.shouldUsePartialData(state, !!preview) ||
        isCollectStarsLeafRound;
    const [partial, loadingPartial] = Stream((usePartialData && state.judging_type != JudgingType.Ajs) ? RoundPartialResultSource(state.round_id) : undefined);
    const judgingSettings = RoundItem.getJudgingSettings(state);
    if (!stateData) {
        return (React.createElement("div", { style: { textAlign: "center", margin: "16px 0" } },
            React.createElement(Empty, null)));
    }
    const roundStateHelper = new RoundStateHelper();
    const roundWithFederation = competitionFederation
        ? { ...state, competition_federation: competitionFederation }
        : state;
    roundStateHelper.initialize(stateData, displayTeams || [], roundWithFederation, showTrainees ? (traineeResults || []) : [], judges || [], !!hideMarks, partial || [], resultComments || [], classRounds || [], states || []);
    const filteredCoachTeams = roundStateHelper.getFilteredCoachTeams(coachFilter);
    const hasCoachCollectStarsAccess = !!publicResults &&
        !preview &&
        state.judging_type === JudgingType.CollectStars &&
        RoundItem.filteredCoach(state) &&
        !!filteredCoachTeams &&
        filteredCoachTeams.length > 0;
    const hideCollectStarsSummary = !!publicResults &&
        !preview &&
        state.judging_type === JudgingType.CollectStars &&
        !hasCoachCollectStarsAccess;
    const getJudges = () => {
        if (isCollectStarsLeafRound && !preview) {
            return ['average'];
        }
        let helperJudges = [...(roundStateHelper.getJudges(showTrainees) || [])];
        if (RoundItem.isPointSummary(round) && !helperJudges.includes('multiplier')) {
            helperJudges.push('multiplier');
        }
        return helperJudges;
    };
    const judgeHasSubmitted = (judge_id) => {
        const foundReceipt = receipts && receipts.find((receipt) => (receipt.judge_id == judge_id));
        return !!foundReceipt;
    };
    const getJudgeLabelText = (judge_id) => {
        const aggregateLabelKey = RoundStateHelper.getAggregateJudgeLabelKey(judge_id);
        if (aggregateLabelKey) {
            const label = _T(aggregateLabelKey);
            return typeof label === 'string' ? label : String(label);
        }
        const foundJudge = judges === null || judges === void 0 ? void 0 : judges.find(a => a.judge_id === judge_id);
        let judgeLabel = roundStateHelper.getJudgeName(judge_id) || stateData.judgeNames[judge_id];
        if ((!judgeLabel && foundJudge) || (showTrainees && foundJudge && foundJudge.judge_type === JudgeType.Trainee)) {
            judgeLabel = foundJudge.judge_letter;
        }
        return judgeLabel ? String(judgeLabel) : '';
    };
    const getJudgeName = (judge_id) => {
        const aggregateLabelKey = RoundStateHelper.getAggregateJudgeLabelKey(judge_id);
        if (aggregateLabelKey) {
            const aggregateLabel = _T(aggregateLabelKey);
            const aggregateText = typeof aggregateLabel === 'string' ? aggregateLabel : String(aggregateLabel);
            const shortAggregateText = aggregateText.length > maxJudgeLabelChars ? `${aggregateText.slice(0, maxJudgeLabelChars)}.` : aggregateText;
            return (React.createElement(Tooltip, { title: aggregateText.length > maxJudgeLabelChars ? aggregateText : undefined },
                React.createElement("span", null, shortAggregateText)));
        }
        const foundJudge = judges === null || judges === void 0 ? void 0 : judges.find(a => a.judge_id === judge_id);
        let judgeLetter = getJudgeLabelText(judge_id);
        if ((!judgeLetter && foundJudge) || (showTrainees && foundJudge && foundJudge.judge_type === JudgeType.Trainee)) {
            judgeLetter = foundJudge.judge_letter;
        }
        const fullName = foundJudge ? JudgeItem.getFullName(foundJudge) : undefined;
        const shortJudgeLabel = judgeLetter.length > maxJudgeLabelChars ? `${judgeLetter.slice(0, maxJudgeLabelChars)}.` : judgeLetter;
        return (React.createElement(Space, { size: 2 },
            React.createElement(Tooltip, { title: fullName || (judgeLetter.length > maxJudgeLabelChars ? judgeLetter : undefined) },
                React.createElement("span", null, shortJudgeLabel)),
            round.round_status === RoundStatus.Ready && (judgeHasSubmitted(judge_id) ? React.createElement(LockFilled, { style: { fontSize: 11 } }) : React.createElement(UnlockOutlined, { style: { fontSize: 11 } })),
            foundJudge && [JudgeType.ChiefIn, JudgeType.ChiefOut].includes(foundJudge.judge_type) && React.createElement(StarFilled, { style: { fontSize: 11 } }),
            foundJudge && foundJudge.judge_type === JudgeType.Trainee && React.createElement(AuditOutlined, { style: { fontSize: 11 } })));
    };
    const shouldShowMajorityColumns = () => {
        return !!((settings === null || settings === void 0 ? void 0 : settings.showMajority) && stateData.majority && stateData.majority.some((majority) => Array.isArray(majority)));
    };
    const getMajorityLimit = () => (Math.floor(stateData.judges.length / 2) + 1);
    const renderPreview = !!preview || (isCollectStarsLeafRound && !hideMarks);
    const hasLegacySummaryKeys = !!(stateData &&
        (Object.prototype.hasOwnProperty.call(stateData, 'sums') ||
            Object.prototype.hasOwnProperty.call(stateData, 'groupSums')));
    const showSumColumn = !hideCollectStarsSummary && (roundStateHelper.shouldShowSumColumn(renderPreview, stateData) || hasLegacySummaryKeys);
    const shouldShowSumColumn = () => showSumColumn;
    const summaryField = showSumColumn ? 'sum' : 'placement';
    const renderModel = roundStateHelper.buildResultRenderModel({
        showTrainees,
        preview: renderPreview,
        includeResultComments: showResultComments,
        includePartialRows: !!(displayComponents.length > 0 && Number(state.round_children || 0) === 0),
        filteredCoachTeams,
        components: displayComponents.map(component => ({
            id: component.id,
            name: component.name,
            step: component.step
        })),
        summaryField,
        decimalStep: ((_a = displayComponents[0]) === null || _a === void 0 ? void 0 : _a.step) || 0.1,
        hideMarks: !!hideMarks
    });
    const dataSource = renderModel.rows.map((modelRow, keyIndex) => {
        var _a, _b, _c;
        const teamId = modelRow.teamId;
        const teamIndex = (_b = (_a = stateData.teams) === null || _a === void 0 ? void 0 : _a.findIndex((id) => String(id) === String(teamId))) !== null && _b !== void 0 ? _b : -1;
        const mainTeam = displayTeams === null || displayTeams === void 0 ? void 0 : displayTeams.find(a => a.heats_team_id === teamId);
        const fullTeamName = mainTeam ? HeatsTeamItem.getFullNameWithOrg(mainTeam) : '';
        const canShowCollectStarsSummary = showCollectStarsMarks &&
            (!!preview ||
                RoundItem.shouldShowMarks(state, modelRow.position, teamId, filteredCoachTeams));
        const canShowCollectStarsPartial = showCollectStarsMarks &&
            (!!preview ||
                RoundItem.shouldShowPartial(state, modelRow.position, teamId, filteredCoachTeams));
        const teamData = {
            ...modelRow,
            key: keyIndex,
            teamId,
            fullName: fullTeamName || modelRow.fullName || modelRow.teamName || '',
            cancelled: !!(mainTeam === null || mainTeam === void 0 ? void 0 : mainTeam.team_cancelled)
        };
        if (canShowCollectStarsSummary &&
            (Array.isArray(stateData.sums) ||
                (Array.isArray(childStates) && childStates.length > 0))) {
            RoundStateHelper.mergeCollectStarsSummaryRow({
                row: teamData,
                teamId: teamId,
                teamIndex,
                judgeIds: getJudges(),
                stateData: {
                    sums: stateData.sums,
                    teams: stateData.teams,
                    componentSums: stateData.componentSums
                },
                childStates: childStates,
                componentSums: modelRow.componentSums
            });
        }
        const placement = modelRow.position;
        const placementMeta = modelRow.placementMeta || {};
        let placementLabel = (_c = modelRow.placementLabel) !== null && _c !== void 0 ? _c : '';
        const placementLabelInfo = roundStateHelper.getPlacementLabelInfo(placementLabel, placementMeta);
        switch (placementLabelInfo.type) {
            case 'alternates-level':
                placementLabel = _T('team.merge.level', { value: placementLabelInfo.priority });
                break;
            case 'alternates-through':
                placementLabel = _T('team.merge.through');
                break;
            case 'localized-key':
                placementLabel = _T(placementLabelInfo.key, placementLabelInfo.params);
                break;
            default:
                placementLabel = placementLabelInfo.value;
                break;
        }
        if (mainTeam === null || mainTeam === void 0 ? void 0 : mainTeam.heats_team_target_usermodified) {
            placementLabel = React.createElement(React.Fragment, null,
                placementLabel,
                React.createElement(WarningOutlined, null));
        }
        teamData.placement = modelRow.canShowPlacement ? placementLabel : '';
        if (Array.isArray(modelRow.children) && (!isCollectStarsLeafRound || canShowCollectStarsPartial)) {
            teamData.children = modelRow.children.map((childRow) => {
                const renderedJudgeResults = { ...childRow };
                getJudges().forEach((judgeId) => {
                    var _a, _b;
                    const valueKey = `result_${judgeId}`;
                    const value = renderedJudgeResults[valueKey];
                    if (state.judging_type === JudgingType.Ajs && value && typeof value === 'object' && 'value' in value) {
                        const rawValue = value.value || 0;
                        const isOutsideMedian = !!value.isOutsideMedian;
                        renderedJudgeResults[valueKey] = isOutsideMedian ? (React.createElement(Text, { key: `${teamId}-${childRow.componentId}-${judgeId}`, type: "danger", delete: true }, rawValue)) : (React.createElement("span", { key: `${teamId}-${childRow.componentId}-${judgeId}` }, rawValue));
                        return;
                    }
                    if (canShowCollectStarsPartial) {
                        const isAggregateJudge = RoundStateHelper.isAggregateJudgeId(String(judgeId));
                        const numericValue = typeof value === 'number'
                            ? value
                            : (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))
                                ? Number(value)
                                : undefined);
                        const componentAverage = (_b = (_a = modelRow.componentSums) === null || _a === void 0 ? void 0 : _a[childRow.componentId]) === null || _b === void 0 ? void 0 : _b.m;
                        const starValue = numericValue !== null && numericValue !== void 0 ? numericValue : (isAggregateJudge && typeof componentAverage === 'number'
                            ? componentAverage
                            : undefined);
                        if (typeof starValue !== 'number') {
                            return;
                        }
                        if (isAggregateJudge) {
                            const percentValue = starValue <= 1 ? starValue * 100 : starValue;
                            const boundedPercent = Math.max(0, Math.min(100, percentValue));
                            renderedJudgeResults[valueKey] = (React.createElement(Progress, { percent: boundedPercent, size: "small", showInfo: false, strokeColor: boundedPercent > 50 ? token.colorSuccess : token.colorWarning }));
                            return;
                        }
                        let labelKey = 'round.stars.no';
                        let backgroundColor;
                        if (starValue === 0.5) {
                            labelKey = 'round.stars.part';
                            backgroundColor = 'gold';
                        }
                        else if (starValue === 1) {
                            labelKey = 'round.stars.yes';
                            backgroundColor = 'chartreuse';
                        }
                        renderedJudgeResults[valueKey] = (React.createElement(Tag, { style: { color: 'dimgrey', backgroundColor } }, _T(labelKey)));
                        return;
                    }
                    if (Array.isArray(value) && value.length > 0) {
                        renderedJudgeResults[valueKey] = value.map((remark, remarkIndex) => {
                            const smallString = React.createElement("span", { style: { fontSize: 10 } }, `(${remark.value})`);
                            return (React.createElement("div", { key: `${childRow.componentId}-${judgeId}-remark-${remarkIndex}` },
                                remark.level,
                                smallString));
                        });
                    }
                });
                const component = displayComponents.find(c => c.id === childRow.componentId);
                const localizedComponentName = Localization.getLocalizedString((component === null || component === void 0 ? void 0 : component.name) || childRow.componentName) || childRow.componentId;
                const componentLabel = (React.createElement(Text, { italic: true }, ['acrobatics', 'remarks'].includes(childRow.componentId)
                    ? _T(childRow.componentId === 'acrobatics' ? 'acrobatics.list' : 'remark.list')
                    : localizedComponentName));
                return {
                    ...childRow,
                    teamId: childRow.teamId || `${teamId}-${childRow.componentId}-comp`,
                    teamName: componentLabel,
                    fullName: componentLabel,
                    cancelled: !!(mainTeam === null || mainTeam === void 0 ? void 0 : mainTeam.team_cancelled),
                    ...renderedJudgeResults
                };
            });
        }
        return teamData;
    });
    const highestPriority = stateData.priorities && Object.keys(stateData.priorities).length > 0
        ? Math.max(...Object.values(stateData.priorities).map(value => Number(value !== null && value !== void 0 ? value : 0)))
        : 0;
    const placementSortValue = (item) => {
        if (!RoundItem.usesPlacement(round) || !RoundItem.shouldShowPlacement(round, item.position, item.teamId, filteredCoachTeams)) {
            return null;
        }
        const placementValue = Number.isNaN(Number(item.position)) ? Infinity : Number(item.position);
        if (state.round_num_through === 0 || !RoundItem.usesPriority(round)) {
            return placementValue;
        }
        const priorityValue = item.priority != null ? item.priority : highestPriority + 1;
        return priorityValue + placementValue;
    };
    const startNumberSortValue = (item) => {
        if (item.startNumber !== undefined &&
            item.startNumber !== null &&
            item.startNumber !== '' &&
            !Number.isNaN(Number(item.startNumber))) {
            return Number(item.startNumber);
        }
        return item.startNumber || item.teamName;
    };
    const compareSortValues = (a, b) => {
        if (typeof a === 'string' && typeof b === 'string') {
            return a.localeCompare(b);
        }
        if (a < b)
            return -1;
        if (a > b)
            return 1;
        return 0;
    };
    const sortedData = dataSource;
    const hasExpandableRows = sortedData.some(row => Array.isArray(row.children) && row.children.length > 0);
    const columns = buildResultColumns({
        hasExpandableRows,
        getJudges,
        getJudgeName,
        hideNameColumn,
        settings,
        token,
        judgingSettings,
        state,
        stateData,
        round,
        shouldShowMajorityColumns,
        getMajorityLimit,
        shouldShowSumColumn,
        placementSortValue,
        startNumberSortValue,
        compareSortValues,
        enableColumnSorters
    });
    const traineeJudgeCount = (_b = judges === null || judges === void 0 ? void 0 : judges.filter((a) => a.judge_type === JudgeType.Trainee).length) !== null && _b !== void 0 ? _b : 0;
    const judgeIds = getJudges().filter((judgeId) => !['base', 'requalify', 'partial', 'multiplier', 'average'].includes(judgeId));
    const allowJudgeMarkCountSummary = state.judging_type === JudgingType.Select ||
        (state.judging_type === JudgingType.PartialInput && !RoundItem.isFinalRound(round));
    const showMarkCountSummary = showJudgeMarkCountSummary &&
        allowJudgeMarkCountSummary &&
        judgeMarkCounts &&
        judgeIds.length > 0;
    const showExpandColumn = !publicResults || hasCoachCollectStarsAccess;
    const settingsFooter = state.judging_type === JudgingType.Ajs ? (React.createElement(Space, null,
        _T('classrules.details.round_tolerance.label'),
        React.createElement("strong", null, stateData.tolerance))) : preview && canShowTrainees && traineeJudgeCount > 0 ? (React.createElement(Space, null,
        React.createElement(Checkbox, { checked: showTrainees, onChange: e => onShowTraineesChange === null || onShowTraineesChange === void 0 ? void 0 : onShowTraineesChange(e.target.checked) }, _T('result.trainee')))) : undefined;
    const footer = () => {
        if (!settingsFooter) {
            return undefined;
        }
        return settingsFooter;
    };
    const showFooter = () => !!settingsFooter;
    const summary = () => {
        if (!showMarkCountSummary) {
            return undefined;
        }
        return (React.createElement(Table.Summary.Row, null, columns.map((column, columnIndex) => {
            const dataIndex = column === null || column === void 0 ? void 0 : column.dataIndex;
            let value = '';
            if (typeof dataIndex === 'string' && dataIndex.startsWith('result_')) {
                const judgeId = dataIndex.substring('result_'.length);
                if (judgeMarkCounts && Object.prototype.hasOwnProperty.call(judgeMarkCounts, judgeId)) {
                    value = judgeMarkCounts[judgeId];
                }
            }
            return (React.createElement(Table.Summary.Cell, { key: `summary-${(column === null || column === void 0 ? void 0 : column.key) || columnIndex}`, index: columnIndex, align: column === null || column === void 0 ? void 0 : column.align }, value));
        })));
    };
    return (React.createElement("div", { style: { paddingBottom: 2 } },
        React.createElement(Table, { dataSource: sortedData, columns: columns, scroll: { x: 'max-content' }, pagination: false, loading: loadingPartial, footer: showFooter() ? footer : undefined, summary: summary, expandable: {
                expandRowByClick: true,
                showExpandColumn
            }, bordered: true, rowKey: "teamId", size: "small" })));
};
